import * as React from 'react';
import { X } from 'lucide-react';

/**
 * Modal size options
 * - sm: 480px width for simple confirmations
 * - md: 640px width for standard dialogs
 * - lg: 800px width for complex forms
 * - full: 95vw for maximum content display
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'full';

/**
 * Props for the Modal component
 */
export interface ModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback when the modal should close
   */
  onClose: () => void;
  /**
   * Modal width variant
   * @default 'md'
   */
  size?: ModalSize;
  /**
   * Whether clicking the backdrop closes the modal
   * @default true
   */
  closeOnBackdropClick?: boolean;
  /**
   * Whether pressing Escape closes the modal
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * Modal content (typically Modal.Header, Modal.Body, Modal.Footer)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the modal container
   */
  className?: string;
  /**
   * ID for the modal title element (for aria-labelledby)
   */
  'aria-labelledby'?: string;
  /**
   * ID for the modal description element (for aria-describedby)
   */
  'aria-describedby'?: string;
}

/**
 * Props for Modal.Header
 */
export interface ModalHeaderProps {
  /**
   * Title text for the modal header
   */
  title?: string;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Additional content to render in the header
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Modal.Body
 */
export interface ModalBodyProps {
  /**
   * Body content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Props for Modal.Footer
 */
export interface ModalFooterProps {
  /**
   * Footer content (typically action buttons)
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

// Context for passing modal state to compound components
interface ModalContextValue {
  onClose: () => void;
  titleId: string;
}

const ModalContext = React.createContext<ModalContextValue | null>(null);

function useModalContext(): ModalContextValue {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error('Modal compound components must be used within Modal');
  }
  return context;
}

/**
 * Size configuration for modal widths
 */
const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[800px]',
  full: 'max-w-[95vw]',
};

/**
 * Modal Header component
 */
function ModalHeader({
  title,
  showCloseButton = true,
  children,
  className = '',
}: Readonly<ModalHeaderProps>) {
  const { onClose, titleId } = useModalContext();

  return (
    <div
      className={[
        'flex items-center justify-between',
        'h-[60px] min-h-[60px]',
        'px-6',
        'border-b border-secondary-200',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h2
            id={titleId}
            className="text-lg font-semibold text-secondary-800 truncate"
          >
            {title}
          </h2>
        )}
        {children}
      </div>
      {showCloseButton && (
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 ml-4 p-1 rounded hover:bg-black/5 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
          aria-label="Close modal"
        >
          <X
            className="w-5 h-5 text-secondary-400 hover:text-secondary-600"
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

ModalHeader.displayName = 'Modal.Header';

/**
 * Modal Body component - scrollable content area
 */
function ModalBody({ children, className = '' }: Readonly<ModalBodyProps>) {
  return (
    <div
      className={[
        'flex-1 overflow-y-auto',
        'p-6',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

ModalBody.displayName = 'Modal.Body';

/**
 * Modal Footer component
 */
function ModalFooter({ children, className = '' }: Readonly<ModalFooterProps>) {
  return (
    <div
      className={[
        'flex items-center justify-end gap-3',
        'h-[60px] min-h-[60px]',
        'px-6',
        'border-t border-secondary-200',
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

ModalFooter.displayName = 'Modal.Footer';

// Generate unique ID for accessibility
let modalIdCounter = 0;
function generateModalId(): string {
  return `modal-${Date.now()}-${++modalIdCounter}`;
}

/**
 * Modal component for displaying dialog overlays with focus trap and accessibility support.
 *
 * Designed for insurance industry applications where clear, focused interactions
 * are critical for confirmations, forms, and important actions.
 *
 * Features:
 * - Four size variants (sm, md, lg, full)
 * - Focus trap when open (cycles focus within modal)
 * - Backdrop click and Escape key to close (configurable)
 * - Returns focus to trigger element when closed
 * - Fade + scale animation (200ms)
 * - role="dialog" and aria-modal="true" for accessibility
 * - Compound components (Header, Body, Footer)
 *
 * @example
 * // Basic modal
 * const [open, setOpen] = useState(false);
 * <Modal open={open} onClose={() => setOpen(false)}>
 *   <Modal.Header title="Confirm Action" />
 *   <Modal.Body>
 *     Are you sure you want to continue?
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
 *     <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
 *   </Modal.Footer>
 * </Modal>
 *
 * @example
 * // Large modal with scrollable content
 * <Modal open={open} onClose={handleClose} size="lg">
 *   <Modal.Header title="Policy Details" />
 *   <Modal.Body>
 *     <form>...</form>
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button variant="primary">Save Changes</Button>
 *   </Modal.Footer>
 * </Modal>
 */
const ModalRoot = React.forwardRef<HTMLDialogElement, ModalProps>(
  (
    {
      open,
      onClose,
      size = 'md',
      closeOnBackdropClick = true,
      closeOnEscape = true,
      children,
      className = '',
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
    },
    ref
  ) => {
    const modalRef = React.useRef<HTMLDialogElement>(null);
    const previousActiveElement = React.useRef<HTMLElement | null>(null);
    const [isExiting, setIsExiting] = React.useState(false);
    const [titleId] = React.useState(() => ariaLabelledby || generateModalId());

    // Combine forwarded ref with internal ref
    React.useImperativeHandle(ref, () => modalRef.current as HTMLDialogElement);

    // Handle close with exit animation
    const handleClose = React.useCallback(() => {
      setIsExiting(true);
      // Wait for exit animation to complete
      setTimeout(() => {
        setIsExiting(false);
        onClose();
      }, 200);
    }, [onClose]);

    // Handle escape key
    React.useEffect(() => {
      if (!open || !closeOnEscape) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          handleClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, closeOnEscape, handleClose]);

    // Focus trap
    React.useEffect(() => {
      if (!open) return;

      const modal = modalRef.current;
      /* v8 ignore start */
      if (!modal) return;
      /* v8 ignore stop */

      // Save currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Get all focusable elements within the modal
      const getFocusableElements = () => {
        return modal.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
      };

      // Focus the first focusable element or the modal itself
      const focusableElements = getFocusableElements();
      const firstElement = focusableElements[0];
      if (firstElement) {
        firstElement.focus();
      } else {
        modal.focus();
      }

      // Handle tab key for focus trap
      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        const focusable = getFocusableElements();
        if (focusable.length === 0) return;

        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];

        /* v8 ignore start */
        if (!firstFocusable || !lastFocusable) return;
        /* v8 ignore stop */

        /* v8 ignore start */
        if (event.shiftKey && document.activeElement === firstFocusable) {
          // Shift + Tab: if on first element, go to last
          event.preventDefault();
          lastFocusable.focus();
        } else if (!event.shiftKey && document.activeElement === lastFocusable) {
          // Tab: if on last element, go to first
          event.preventDefault();
          firstFocusable.focus();
        }
        /* v8 ignore stop */
      };

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }, [open]);

    // Return focus when closing
    React.useEffect(() => {
      if (!open && previousActiveElement.current) {
        previousActiveElement.current.focus();
        previousActiveElement.current = null;
      }
    }, [open]);

    // Lock body scroll when open
    React.useEffect(() => {
      if (open) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
    }, [open]);

    const contextValue = React.useMemo<ModalContextValue>(
      () => ({
        onClose: handleClose,
        titleId,
      }),
      [handleClose, titleId]
    );

    if (!open && !isExiting) return null;

    const animationClass = isExiting
      ? 'animate-modal-exit'
      : 'animate-modal-enter';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop button - handles click-outside-to-close */}
        <button
          type="button"
          className={[
            'absolute inset-0 w-full h-full',
            'border-none cursor-default',
            isExiting ? 'animate-modal-backdrop-exit' : 'animate-modal-backdrop-enter',
          ].join(' ')}
          style={{ backgroundColor: 'rgba(28, 25, 23, 0.5)' }} // neutral-900 at 50%
          onClick={closeOnBackdropClick ? handleClose : undefined}
          aria-label="Close modal by clicking backdrop"
          tabIndex={-1}
        />
        <dialog
          ref={modalRef}
          open
          aria-modal="true"
          aria-labelledby={ariaLabelledby || titleId}
          aria-describedby={ariaDescribedby}
          tabIndex={-1}
          className={[
            'relative',
            'w-full',
            sizeStyles[size],
            'max-h-[85vh]',
            'flex flex-col',
            'bg-secondary-100',
            'rounded-xl',
            'shadow-xl',
            animationClass,
            // Support for reduced motion
            'motion-reduce:animate-none',
            className,
          ].filter(Boolean).join(' ')}
        >
          <ModalContext.Provider value={contextValue}>
            {children}
          </ModalContext.Provider>
        </dialog>
      </div>
    );
  }
);

ModalRoot.displayName = 'Modal';

/**
 * Modal compound component with Header, Body, and Footer
 */
export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});
